import { v4 as uuidv4 } from 'uuid';
import { IAudioNoteDescription } from 'src/models/audioNotes';
import { erebus, FILE_TRANSFER_TIMEOUT_MS } from 'boot/axios';
import { DurationValidation } from 'src/composables/useAudioRecorder';

/**
 * Parameters for saving an audio note
 */
export interface SaveAudioNoteParams {
  caseID: string;
  clientID: number;
  locationID: number;
  durationMs: number;
  audioBuffer: ArrayBuffer;
}

/**
 * Custom error class for duration validation failures
 */
export class DurationValidationError extends Error {
  constructor(
    message: string,
    public errorType: 'invalid' | 'tooShort' | 'tooLong',
  ) {
    super(message);
    this.name = 'DurationValidationError';
  }
}

/**
 * Service for managing audio note operations
 *
 * Handles the business logic for creating and saving audio notes,
 * including validation and API calls.
 *
 * Presentation concerns (toasts, UI feedback) are handled by the calling component.
 * State management concerns (caching) are handled by the store.
 */
export default class AudioNoteService {
  /**
   * Validate audio note duration
   *
   * @param validation - Duration validation result
   * @throws DurationValidationError if validation fails
   */
  validateDuration(validation: DurationValidation): void {
    if (!validation.valid) {
      throw new DurationValidationError(
        `Duration validation failed: ${validation.errorType}`,
        validation.errorType!,
      );
    }
  }

  /**
   * Save an audio note
   *
   * Creates audio note metadata and uploads the audio file via API.
   *
   * @param params - Audio note parameters
   * @throws Error if save fails
   */
  async saveAudioNote(params: SaveAudioNoteParams): Promise<void> {
    const desc: IAudioNoteDescription = {
      ID: uuidv4(),
      caseID: params.caseID,
      clientID: params.clientID,
      locationID: params.locationID,
      fileName: `audio-note-${uuidv4()}.webm`,
      duration: params.durationMs,
    };

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('meta', JSON.stringify(desc));
    formData.append('file', new Blob([params.audioBuffer], { type: 'audio/webm' }));

    // Post to API
    await erebus.post('/audio-notes', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: FILE_TRANSFER_TIMEOUT_MS,
    });
  }
}
